import React, { useState, useEffect, useCallback } from 'react';
import { Bot, Copy, Check, Terminal, Wifi, BookMarked, RotateCcw } from 'lucide-react';

const SESSION_KEY = '_tv_bridge_tab_id';

function getBridgeId() {
  return sessionStorage.getItem(SESSION_KEY) || null;
}

function getBridgeState() {
  const bridge = window._tvBridge;
  if (!bridge) return { wsConnected: false, isActive: false };
  return { wsConnected: bridge.wsConnected, isActive: bridge.isActive };
}

export default function AICopilotTab() {
  const [tabId, setTabId] = useState(() => getBridgeId());
  const [bridgeState, setBridgeState] = useState(getBridgeState);
  const [copied, setCopied] = useState(false);
  const [savedCharts, setSavedCharts] = useState([]);
  const [loadingCharts, setLoadingCharts] = useState(false);
  const [loadingId, setLoadingId] = useState(null);

  useEffect(() => {
    const onStateChange = (e) => {
      setBridgeState({ wsConnected: e.detail.wsConnected, isActive: e.detail.isActive });
      if (e.detail.id && !tabId) setTabId(e.detail.id);
    };
    window.addEventListener('tvbridge:statechange', onStateChange);

    const syncNow = () => {
      const id = getBridgeId();
      if (id) setTabId(id);
      setBridgeState(getBridgeState());
    };
    syncNow();

    const poll = setInterval(() => {
      const id = getBridgeId();
      if (id) setTabId(id);
      const state = getBridgeState();
      setBridgeState(prev =>
        prev.wsConnected !== state.wsConnected || prev.isActive !== state.isActive ? state : prev
      );
    }, 500);

    return () => {
      window.removeEventListener('tvbridge:statechange', onStateChange);
      clearInterval(poll);
    };
  }, [tabId]);

  // Fetch saved charts from TradingView's getSavedCharts() via the bridge.
  const fetchSavedCharts = useCallback(async () => {
    const bridge = window._tvBridge;
    if (!bridge || !bridge.wsConnected) return;
    setLoadingCharts(true);
    try {
      const result = await bridge.exec('eval', {
        code: `(async () => new Promise((resolve, reject) => {
          try { tvWidget.getSavedCharts(charts => resolve(charts)); }
          catch (e) { reject(String(e)); }
        }))()`
      });
      if (Array.isArray(result)) setSavedCharts(result);
    } catch {
      // Bridge not ready or no saved charts — silently ignore
    } finally {
      setLoadingCharts(false);
    }
  }, []);

  // Load a saved chart by ID via the bridge.
  const loadChart = useCallback(async (chartId) => {
    const bridge = window._tvBridge;
    if (!bridge) return;
    setLoadingId(chartId);
    try {
      await bridge.exec('eval', { code: `tvWidget.loadChartFromServer(${chartId})` });
    } finally {
      setLoadingId(null);
    }
  }, []);

  // Auto-fetch saved charts when bridge becomes active.
  useEffect(() => {
    if (bridgeState.wsConnected) fetchSavedCharts();
  }, [bridgeState.wsConnected, fetchSavedCharts]);

  const shortId = tabId ? tabId.slice(0, 8) : null;
  const connectCmd = shortId ? `tv use ${shortId}` : 'tv use <session-id>';

  const copy = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const { wsConnected, isActive } = bridgeState;
  const status = isActive ? 'active' : wsConnected ? 'connected' : 'offline';

  const statusConfig = {
    offline: {
      dot: 'bg-gray-500',
      label: 'Not connected',
      labelColor: 'text-gray-500',
    },
    connected: {
      dot: 'bg-yellow-400 animate-pulse',
      label: 'Bridge ready',
      labelColor: 'text-yellow-400',
    },
    active: {
      dot: 'bg-green-400',
      label: 'AI connected',
      labelColor: 'text-green-400',
    },
  };

  const { dot, label, labelColor } = statusConfig[status];

  return (
    <div className="p-4 space-y-5">

      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-gray-700 flex items-center justify-center flex-shrink-0">
          <Bot size={20} className="text-blue-400" />
        </div>
        <div>
          <div className="text-sm font-semibold text-gray-200">AI Chart Copilot</div>
          <div className="text-xs text-gray-500">Connect Claude to control this chart</div>
        </div>
        <div className="ml-auto flex items-center gap-1.5">
          <div className={`w-2 h-2 rounded-full ${dot}`} />
          <span className={`text-xs ${labelColor}`}>{label}</span>
        </div>
      </div>

      {/* Session ID */}
      <div className="bg-gray-800/60 border border-gray-700 rounded-xl p-3 space-y-2">
        <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Session ID</div>
        {tabId ? (
          <>
            <div className="flex items-center gap-2">
              <code className="flex-1 bg-gray-900 text-blue-300 text-xs rounded px-2.5 py-2 font-mono tracking-wide">
                {shortId}
              </code>
              <button
                onClick={() => copy(shortId)}
                title="Copy session ID"
                className="p-1.5 rounded text-gray-400 hover:text-gray-200 hover:bg-gray-700 transition-colors"
              >
                {copied ? <Check size={13} className="text-green-400" /> : <Copy size={13} />}
              </button>
            </div>
            <p className="text-xs text-gray-500 leading-relaxed">
              This ID identifies this chart tab. Give it to Claude so it knows which chart to control.
            </p>
          </>
        ) : (
          <p className="text-xs text-gray-500 italic">Waiting for chart to load…</p>
        )}
      </div>

      {/* State-specific instructions */}
      {status === 'offline' && (
        <div className="space-y-3">
          <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Setup</div>

          <div className="flex items-start gap-3">
            <div className="w-5 h-5 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0 text-xs font-bold text-white mt-0.5">1</div>
            <div className="flex-1">
              <p className="text-xs text-gray-300 mb-1.5">Start the CLI server in your terminal:</p>
              <div className="bg-gray-900 rounded-lg border border-gray-700 px-3 py-2 flex items-center gap-2">
                <Terminal size={11} className="text-gray-500 flex-shrink-0" />
                <code className="text-xs text-green-400 font-mono">traderbro tv serve</code>
              </div>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <div className="w-5 h-5 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0 text-xs font-bold text-white mt-0.5">2</div>
            <p className="text-xs text-gray-300">
              Once connected, the status above will turn yellow. Then tell Claude which chart to control using the Session ID.
            </p>
          </div>
        </div>
      )}

      {status === 'connected' && tabId && (
        <div className="space-y-3">
          <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Connect Claude</div>
          <p className="text-xs text-gray-300 leading-relaxed">
            Claude Code is running but hasn't targeted this chart yet. Run this in Claude:
          </p>
          <div className="bg-gray-900 rounded-lg border border-gray-700 p-3 flex items-center gap-2 relative">
            <Terminal size={11} className="text-gray-500 flex-shrink-0" />
            <code className="flex-1 text-xs text-green-400 font-mono">{connectCmd}</code>
            <button
              onClick={() => copy(connectCmd)}
              className="p-1 rounded text-gray-400 hover:text-gray-200 hover:bg-gray-700 transition-colors"
            >
              {copied ? <Check size={13} className="text-green-400" /> : <Copy size={13} />}
            </button>
          </div>
          <p className="text-xs text-gray-500">
            Or tell Claude: "connect to chart session {shortId}"
          </p>
        </div>
      )}

      {status === 'active' && (
        <div className="bg-green-950/40 border border-green-800/50 rounded-xl p-3 space-y-1.5">
          <div className="flex items-center gap-2">
            <Wifi size={13} className="text-green-400" />
            <span className="text-xs font-semibold text-green-300">Claude is controlling this chart</span>
          </div>
          <p className="text-xs text-gray-400 leading-relaxed">
            Claude can change the symbol, timeframe, draw patterns, add studies, and take screenshots.
          </p>
          {tabId && (
            <p className="text-xs text-gray-600 font-mono">session: {shortId}</p>
          )}
        </div>
      )}

      {/* What Claude can do */}
      <div className="pt-1 border-t border-gray-700/60 space-y-1.5">
        <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">What Claude can do</div>
        {[
          'Change symbol and timeframe',
          'Identify and draw chart patterns',
          'Add and remove indicators',
          'Read bar data for precise analysis',
          'Take screenshots for visual review',
          'Save annotated charts with tv save',
        ].map(item => (
          <div key={item} className="flex items-center gap-2 text-xs text-gray-500">
            <div className="w-1 h-1 rounded-full bg-gray-600 flex-shrink-0" />
            {item}
          </div>
        ))}
      </div>

      {/* Saved Charts — visible when bridge is connected */}
      {(status === 'connected' || status === 'active') && (
        <div className="pt-1 border-t border-gray-700/60 space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              <BookMarked size={11} className="text-gray-500" />
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Saved Charts</div>
            </div>
            <button
              onClick={fetchSavedCharts}
              disabled={loadingCharts}
              title="Refresh list"
              className="p-1 rounded text-gray-600 hover:text-gray-400 hover:bg-gray-700 transition-colors disabled:opacity-40"
            >
              <RotateCcw size={11} className={loadingCharts ? 'animate-spin' : ''} />
            </button>
          </div>

          {savedCharts.length === 0 ? (
            <p className="text-xs text-gray-600 italic leading-relaxed">
              No saved charts yet. After annotating, run{' '}
              <code className="text-green-500 not-italic">tv save</code> in the CLI.
            </p>
          ) : (
            <div className="space-y-1.5 max-h-52 overflow-y-auto pr-0.5">
              {savedCharts.map(chart => (
                <div
                  key={chart.id}
                  className="flex items-center gap-2 bg-gray-800/50 border border-gray-700/50 rounded-lg px-2.5 py-2"
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-gray-200 truncate font-medium leading-tight">
                      {chart.name}
                    </div>
                    <div className="text-xs text-gray-600 mt-0.5">
                      {chart.short_symbol}
                      {chart.interval ? ` · ${chart.interval}` : ''}
                    </div>
                  </div>
                  <button
                    onClick={() => loadChart(chart.id)}
                    disabled={loadingId === chart.id}
                    className="text-xs text-blue-400 hover:text-blue-300 px-2 py-1 rounded
                               hover:bg-gray-700 transition-colors flex-shrink-0 disabled:opacity-40"
                  >
                    {loadingId === chart.id ? '…' : 'Load'}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

    </div>
  );
}
